import { Updater, VisibilityState } from '@tanstack/react-table'
import { useUiPreferences } from 'hooks/uiPreferences'
import { EventsColumnState } from 'models'
import { useCallback, useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react'

import { DEFAULT_COLUMN_VISIBILITY } from './columns'
import {
  applyFeatureRestrictions,
  computeInitialOrder,
  computeInitialVisibility,
  createPersistableState,
  orderEquals,
  reconcileColumnOrder,
  visibilityEquals,
} from './columnState'

export interface ColumnStateConfig {
  subscriptionId: string
  allColumnIds: string[]
  disabledColumnIds: Set<string>
}

export interface ColumnState {
  visibility: VisibilityState
  order: string[]
}

export interface ColumnStateActions {
  setVisibility: (visibilityOrUpdater: Updater<VisibilityState>) => void
  setOrder: (orderOrUpdater: Updater<string[]>) => void
}

export interface UseColumnStateResult extends ColumnState, ColumnStateActions {
  isInitialized: boolean
}

/**
 * Manages column visibility and order state with persistence.
 *
 * Design principles:
 * 1. State initialization happens in useEffect (not during render)
 * 2. Persistence is fire-and-forget (writes don't trigger re-renders)
 * 3. Feature restrictions are always enforced
 * 4. New columns are automatically appended to existing orders
 * 5. Equality checks prevent unnecessary updates and render loops
 *
 * Note: This hook consumes useUiPreferences, so the caller will re-render when
 * any uiPreferences change. However, the internal column state is managed via
 * useSyncExternalStore and only updates when column-specific preferences change.
 * To avoid initialization overhead in client mode, pass empty allColumnIds.
 */
export function useColumnState(config: ColumnStateConfig): UseColumnStateResult {
  const { subscriptionId, allColumnIds, disabledColumnIds } = config
  const { uiPreferences, updateUiPreferences } = useUiPreferences()

  // Extract stored preferences for this subscription.
  // We only need this for initialization - after that, we manage our own state
  // and persist changes back to uiPreferences (one-way sync).
  const storedPrefs = uiPreferences?.eventsColumn?.[subscriptionId]

  // Keep config values in refs to avoid recreating callbacks.
  // Updated in useLayoutEffect to ensure refs are current before any user interaction
  // (useEffect would leave a window where callbacks could use stale values).
  const disabledColumnIdsRef = useRef(disabledColumnIds)
  const subscriptionIdRef = useRef(subscriptionId)

  useLayoutEffect(() => {
    disabledColumnIdsRef.current = disabledColumnIds
    subscriptionIdRef.current = subscriptionId
  }, [disabledColumnIds, subscriptionId])

  // Track initialization state
  const initializedForRef = useRef<string | null>(null)
  const isInitializedRef = useRef(false)

  // Track if we've already processed the current storedPrefs to avoid
  // re-processing on every uiPreferences change (which happens after persist)
  const processedPrefsRef = useRef<{ subscriptionId: string; visibility?: unknown; order?: unknown } | null>(null)

  // Track previous allColumnIds to detect when new columns are added
  const prevAllColumnIdsRef = useRef<string[]>([])

  // Current state - initialize with defaults, will be updated in effect
  const stateRef = useRef<ColumnState>({
    visibility: DEFAULT_COLUMN_VISIBILITY,
    order: [],
  })

  // Subscribers for useSyncExternalStore
  const subscribersRef = useRef(new Set<() => void>())

  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback)
    return () => subscribersRef.current.delete(callback)
  }, [])

  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach((callback) => callback())
  }, [])

  // Persist to uiPreferences (fire-and-forget, doesn't affect local state)
  const persist = useCallback(
    (state: EventsColumnState) => {
      updateUiPreferences((prev) => ({
        ...prev,
        eventsColumn: { ...prev.eventsColumn, [subscriptionIdRef.current]: state },
      }))
    },
    [updateUiPreferences]
  )

  const setVisibility = useCallback(
    (visibilityOrUpdater: Updater<VisibilityState>) => {
      // Resolve updater function if provided (TanStack can pass either value or updater)
      const visibility =
        typeof visibilityOrUpdater === 'function'
          ? visibilityOrUpdater(stateRef.current.visibility)
          : visibilityOrUpdater

      // Apply feature restrictions using ref to avoid recreating callback
      const effective = applyFeatureRestrictions(visibility, disabledColumnIdsRef.current)

      // Skip if unchanged - critical for preventing render loops
      if (visibilityEquals(effective, stateRef.current.visibility)) {
        return
      }

      stateRef.current = { ...stateRef.current, visibility: effective }

      // Notify subscribers and persist synchronously. We don't use startTransition
      // because deferring persist() causes it to trigger a separate update cycle,
      // which actually increases total render time.
      notifySubscribers()
      persist(createPersistableState(effective, stateRef.current.order))
    },
    [notifySubscribers, persist]
  )

  const setOrder = useCallback(
    (orderOrUpdater: Updater<string[]>) => {
      // Resolve updater function if provided (TanStack can pass either value or updater)
      const order = typeof orderOrUpdater === 'function' ? orderOrUpdater(stateRef.current.order) : orderOrUpdater

      // Skip if unchanged - critical for preventing render loops
      if (orderEquals(order, stateRef.current.order)) {
        return
      }

      stateRef.current = { ...stateRef.current, order }

      // Notify subscribers and persist synchronously (see setVisibility comment)
      notifySubscribers()
      persist(createPersistableState(stateRef.current.visibility, order))
    },
    [notifySubscribers, persist]
  )

  // Initialize or update state when config changes
  // Using useEffect ensures we don't mutate refs during render (React rules)
  useEffect(() => {
    // Can't initialize without column definitions
    if (allColumnIds.length === 0) {
      isInitializedRef.current = false
      return
    }

    // If subscription changed, reinitialize from stored preferences
    if (initializedForRef.current !== subscriptionId) {
      const visibility = computeInitialVisibility(allColumnIds, storedPrefs?.visibility, disabledColumnIds)
      const order = computeInitialOrder(allColumnIds, storedPrefs?.order)

      // Only update if actually different (prevents unnecessary renders)
      if (!visibilityEquals(visibility, stateRef.current.visibility) || !orderEquals(order, stateRef.current.order)) {
        stateRef.current = { visibility, order }
        notifySubscribers()
      }

      // Persist if computed state differs from stored (e.g., new columns were appended)
      const storedVisibility = storedPrefs?.visibility ?? {}
      const storedOrder = storedPrefs?.order ?? []
      if (!visibilityEquals(visibility, storedVisibility) || !orderEquals(order, storedOrder)) {
        persist(createPersistableState(visibility, order))
      }

      // Track that we've processed these prefs so we don't reprocess after persist
      processedPrefsRef.current = {
        subscriptionId,
        visibility: storedPrefs?.visibility,
        order: storedPrefs?.order,
      }

      // Track allColumnIds so reconciliation only runs when it changes
      prevAllColumnIdsRef.current = allColumnIds

      initializedForRef.current = subscriptionId
      isInitializedRef.current = true
      return
    }

    // Already initialized - skip if storedPrefs changed due to our own persist()
    // This prevents a second re-render after we persist our state change.
    // Check both visibility and order since either can change from persist().
    const prefsChanged =
      processedPrefsRef.current?.visibility !== storedPrefs?.visibility ||
      processedPrefsRef.current?.order !== storedPrefs?.order
    if (processedPrefsRef.current?.subscriptionId === subscriptionId && prefsChanged) {
      // storedPrefs changed - this is from our persist(), update ref and skip
      processedPrefsRef.current = {
        subscriptionId,
        visibility: storedPrefs?.visibility,
        order: storedPrefs?.order,
      }
      return
    }

    // Check for reconciliation needs (new columns added, feature flags changed)
    let needsUpdate = false
    let newState = stateRef.current

    // Only reconcile order when allColumnIds actually changes (new columns added to codebase).
    // Don't reconcile on every render, as that would undo user's explicit order changes.
    const allColumnIdsChanged = !orderEquals(prevAllColumnIdsRef.current, allColumnIds)
    if (allColumnIdsChanged) {
      prevAllColumnIdsRef.current = allColumnIds
      const reconciledOrder = reconcileColumnOrder(stateRef.current.order, allColumnIds)
      if (reconciledOrder) {
        newState = { ...newState, order: reconciledOrder }
        needsUpdate = true
      }
    }

    // Check if feature restrictions changed
    const restrictedVisibility = applyFeatureRestrictions(newState.visibility, disabledColumnIds)
    if (restrictedVisibility !== newState.visibility) {
      newState = { ...newState, visibility: restrictedVisibility }
      needsUpdate = true
    }

    if (needsUpdate) {
      stateRef.current = newState
      notifySubscribers()
      // Persist reconciled state so new columns are saved to localStorage
      persist(createPersistableState(newState.visibility, newState.order))
    }

    isInitializedRef.current = true
  }, [
    subscriptionId,
    allColumnIds,
    disabledColumnIds,
    storedPrefs?.visibility,
    storedPrefs?.order,
    notifySubscribers,
    persist,
  ])

  // Use useSyncExternalStore for correct concurrent mode behavior
  const state = useSyncExternalStore(
    subscribe,
    () => stateRef.current,
    () => stateRef.current
  )

  return {
    visibility: state.visibility,
    order: state.order,
    setVisibility,
    setOrder,
    isInitialized: isInitializedRef.current,
  }
}

/**
 * Simpler hook for client-side column state (no persistence).
 */
export function useClientColumnState(
  initialVisibility: VisibilityState = {},
  initialOrder: string[] = []
): UseColumnStateResult {
  const stateRef = useRef<ColumnState>({
    visibility: initialVisibility,
    order: initialOrder,
  })

  const subscribersRef = useRef(new Set<() => void>())
  const subscribe = useCallback((callback: () => void) => {
    subscribersRef.current.add(callback)
    return () => subscribersRef.current.delete(callback)
  }, [])

  const notifySubscribers = useCallback(() => {
    subscribersRef.current.forEach((callback) => callback())
  }, [])

  const setVisibility = useCallback(
    (visibilityOrUpdater: Updater<VisibilityState>) => {
      const visibility =
        typeof visibilityOrUpdater === 'function'
          ? visibilityOrUpdater(stateRef.current.visibility)
          : visibilityOrUpdater
      if (visibilityEquals(visibility, stateRef.current.visibility)) {
        return
      }
      stateRef.current = { ...stateRef.current, visibility }
      notifySubscribers()
    },
    [notifySubscribers]
  )

  const setOrder = useCallback(
    (orderOrUpdater: Updater<string[]>) => {
      const order = typeof orderOrUpdater === 'function' ? orderOrUpdater(stateRef.current.order) : orderOrUpdater
      if (orderEquals(order, stateRef.current.order)) {
        return
      }
      stateRef.current = { ...stateRef.current, order }
      notifySubscribers()
    },
    [notifySubscribers]
  )

  const state = useSyncExternalStore(
    subscribe,
    () => stateRef.current,
    () => stateRef.current
  )

  return {
    visibility: state.visibility,
    order: state.order,
    setVisibility,
    setOrder,
    isInitialized: true,
  }
}
